import { Clock, Eye, LayoutDashboard, Pencil, Share2, ShieldCheck } from "lucide-react";
import type { MicroAppRole, MicroAppSummary } from "@/types";
import { formatRelativeTime } from "./relativeTime";

function RoleBadge({ role }: Readonly<{ role: MicroAppRole | null }>) {
  if (role === "admin") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-brand-primary">
        <ShieldCheck className="w-3 h-3" /> Admin
      </span>
    );
  }
  if (role === "editor") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-blue-400">
        <Pencil className="w-3 h-3" /> Editor
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-content-tertiary">
      <Eye className="w-3 h-3" /> Viewer
    </span>
  );
}

/** A single micro app card in the gallery grid. */
export function MicroAppCard({ app, onOpen }: Readonly<{ app: MicroAppSummary; onOpen: (uuid: string) => void }>) {
  return (
    <button
      type="button"
      onClick={() => onOpen(app.uuid)}
      className="text-left p-4 rounded-xl border border-border-primary bg-surface-primary hover:border-brand-primary/50 transition group"
    >
      <div className="flex items-start gap-3">
        <div
          className="h-10 w-10 shrink-0 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${app.color}1A`, color: app.color }}
        >
          <LayoutDashboard className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-content-primary truncate group-hover:text-brand-primary transition">
              {app.name}
            </h3>
            {app.isShared && (
              <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-brand-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-primary">
                <Share2 className="w-2.5 h-2.5" /> Shared
              </span>
            )}
          </div>
          {app.description && <p className="text-sm text-content-tertiary line-clamp-2 mt-0.5">{app.description}</p>}
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2">
            <RoleBadge role={app.currentUserRole} />
            {app.isShared && app.createdByName ? (
              <span className="text-xs text-content-quaternary">Shared by {app.createdByName}</span>
            ) : (
              app.createdByName && <span className="text-xs text-content-quaternary">by {app.createdByName}</span>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-content-quaternary">
              <Clock className="w-3 h-3" /> {formatRelativeTime(app.updatedAt)}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}
